/**
 * Trend Monster Strategy — faithful port of MT4 MT4_Trend_Monster_2.1.mq4
 *
 * Uses DUAL SuperTrend system (TM.mq4 algorithm run twice):
 *   - ST1 & ST2 with configurable period/multiplier per preset
 *   - Signal fires when BOTH SuperTrends agree on direction
 *   - Goes NEUTRAL when STs disagree (ST_SIGNAL_ON_BOTH_MATCHING = true)
 *
 * Presets:
 *   Standard   → ST1(7, 4.0) + ST2(10, 5.0)
 *   Long Trend → ST1(3, 10.0) + ST2(5, 10.0)
 *   NSE        → ST1(9, 4.0) + ST2(9, 4.0)
 *
 * SL/TP (Percentage-based from MT4):
 *   SL  = 0.75%
 *   TP1 = 0.50%
 *   TP2 = 1.00%
 *   TSL = 0.35% (percentage trailing, ratchets only in favour)
 *
 * MT4 features ported:
 *   - Dual SuperTrend confirmation with NEUTRAL on disagreement
 *   - Buy/Sell arrow markers
 *   - Custom colored candles: Green (#008000), Red (#FF0000), White (#FFFFFF)
 *   - Percentage-based SL/TP/TSL (with DIGITS=2 rounding to 0.05)
 *   - Trailing stop with activation threshold
 *   - Target/SL hit detection with markers
 *   - Dashboard metadata for overlay component
 *
 * Category: Strategies | Pane: Overlay
 */
import { IndicatorDefinition, PlotData, IndicatorMarker, IndicatorPriceLine } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { inferTickSize, roundToTick } from "./price-utils";

// ─── Constants ───────────────────────────────────────────────────
const DIR_NONE = 0;
const DIR_LONG = 1;
const DIR_SHORT = -1;
type Direction = typeof DIR_NONE | typeof DIR_LONG | typeof DIR_SHORT;

const SL_PERCENT = 0.75;
const TP1_PERCENT = 0.50;
const TP2_PERCENT = 1.00;
const TSL_PERCENT = 0.35;
const TSL_ACTIVATE_PERCENT = 0.15; // TSL activates after 0.15% move in favour

// ─── Presets ─────────────────────────────────────────────────────
interface Preset {
  st1Period: number; st1Mult: number;
  st2Period: number; st2Mult: number;
}
const PRESETS: Record<string, Preset> = {
  standard:   { st1Period: 7,  st1Mult: 4.0, st2Period: 10, st2Mult: 5.0 },
  long_trend: { st1Period: 3,  st1Mult: 10.0, st2Period: 5, st2Mult: 10.0 },
  nse:        { st1Period: 9,  st1Mult: 4.0, st2Period: 9,  st2Mult: 4.0 },
};

// ─── ATR computation (Wilder smoothing) ──────────────────────────
function computeATR(candles: CandleData[], period: number): number[] {
  const atr: number[] = new Array(candles.length).fill(0);
  if (candles.length < 2) return atr;
  const tr: number[] = [candles[0].high - candles[0].low];
  for (let i = 1; i < candles.length; i++) {
    const h = candles[i].high, l = candles[i].low, pc = candles[i - 1].close;
    tr.push(Math.max(h - l, Math.abs(h - pc), Math.abs(l - pc)));
  }
  let sum = 0;
  for (let i = 0; i < Math.min(period, tr.length); i++) sum += tr[i];
  atr[period - 1] = sum / period;
  for (let i = period; i < tr.length; i++) {
    atr[i] = (atr[i - 1] * (period - 1) + tr[i]) / period;
  }
  return atr;
}

// ─── SuperTrend computation (TM.mq4 algorithm) ──────────────────
function computeSuperTrend(
  candles: CandleData[], period: number, multiplier: number,
): { direction: Direction[] } {
  const len = candles.length;
  const atr = computeATR(candles, period);
  const upper: number[] = new Array(len).fill(0);
  const lower: number[] = new Array(len).fill(0);
  const direction: Direction[] = new Array(len).fill(DIR_LONG);

  for (let i = 0; i < len; i++) {
    const mid = (candles[i].high + candles[i].low) / 2;
    upper[i] = mid + multiplier * atr[i];
    lower[i] = mid - multiplier * atr[i];
  }

  for (let i = 1; i < len; i++) {
    const c = candles[i].close;
    if (c > upper[i - 1]) direction[i] = DIR_LONG;
    else if (c < lower[i - 1]) direction[i] = DIR_SHORT;
    else direction[i] = direction[i - 1];

    if (direction[i] === DIR_LONG && lower[i] < lower[i - 1]) lower[i] = lower[i - 1];
    if (direction[i] === DIR_SHORT && upper[i] > upper[i - 1]) upper[i] = upper[i - 1];

    const prev = direction[i - 1];
    if (direction[i] === DIR_SHORT && prev === DIR_LONG) upper[i] = (candles[i].high + candles[i].low) / 2 + multiplier * atr[i];
    if (direction[i] === DIR_LONG && prev === DIR_SHORT) lower[i] = (candles[i].high + candles[i].low) / 2 - multiplier * atr[i];
  }

  return { direction };
}

// ─── EMA computation (for strength meter) ────────────────────────
function computeEMA(candles: CandleData[], period: number): number[] {
  const ema: number[] = new Array(candles.length).fill(0);
  if (candles.length === 0) return ema;
  const k = 2.0 / (period + 1);
  let sum = 0;
  for (let i = 0; i < Math.min(period, candles.length); i++) sum += candles[i].close;
  ema[Math.min(period - 1, candles.length - 1)] = sum / Math.min(period, candles.length);
  for (let i = period; i < candles.length; i++) {
    ema[i] = candles[i].close * k + ema[i - 1] * (1 - k);
  }
  return ema;
}

// ─── RSI computation ─────────────────────────────────────────────
function computeRSI(candles: CandleData[], period: number): number[] {
  const rsi: number[] = new Array(candles.length).fill(50);
  if (candles.length < period + 1) return rsi;
  let gainSum = 0, lossSum = 0;
  for (let i = 1; i <= period; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    if (diff > 0) gainSum += diff; else lossSum += Math.abs(diff);
  }
  let avgGain = gainSum / period;
  let avgLoss = lossSum / period;
  rsi[period] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  for (let i = period + 1; i < candles.length; i++) {
    const diff = candles[i].close - candles[i - 1].close;
    avgGain = (avgGain * (period - 1) + (diff > 0 ? diff : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (diff < 0 ? Math.abs(diff) : 0)) / period;
    rsi[i] = avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss);
  }
  return rsi;
}

// ─── Pivot Points computation (Standard) ─────────────────────────
const IST_OFFSET_SEC = 5 * 3600 + 30 * 60;

function getISTDate(unixSec: number): number {
  const d = new Date((unixSec + IST_OFFSET_SEC) * 1000);
  return d.getUTCFullYear() * 10000 + (d.getUTCMonth() + 1) * 100 + d.getUTCDate();
}

interface PivotLevels {
  pp: number; r1: number; r2: number; r3: number;
  s1: number; s2: number; s3: number;
}

function computePivotLevels(prevHigh: number, prevLow: number, prevClose: number): PivotLevels {
  const pp = (prevHigh + prevLow + prevClose) / 3;
  const range = prevHigh - prevLow;
  return {
    pp,
    r1: 2 * pp - prevLow,
    r2: pp + range,
    r3: prevHigh + 2 * (pp - prevLow),
    s1: 2 * pp - prevHigh,
    s2: pp - range,
    s3: prevLow - 2 * (prevHigh - pp),
  };
}

// ─── Strength meter (0–5 score) ──────────────────────────────────
type StrengthLevel = "Weak" | "Medium" | "Strong";

function computeStrengthLabel(score: number): StrengthLevel {
  if (score <= 2) return "Weak";
  if (score <= 3) return "Medium";
  return "Strong";
}

// ─── Dashboard data interface ────────────────────────────────────
export interface TrendMonsterDashboard {
  signal: "BUY" | "SELL" | null;
  trend: "LONG" | "SHORT" | "NEUTRAL";
  entry: number | null;
  stopLoss: number | null;
  trailingSL: number | null;
  target1: number | null;
  target2: number | null;
  currentPrice: number;
  livePL: number;
  bookedPL: number;
  status: "Active" | "Target1 Hit" | "Target2 Hit" | "Stop Loss Hit" | "Trailing SL Hit";
  tp1Hit: boolean;
  tp2Hit: boolean;
  slHit: boolean;
  tslHit: boolean;
  preset: string;
  candleState: "long" | "short" | "neutral";
  /** Strength meter: 0-5 score indicating trend strength */
  strengthScore: number;
  strengthLabel: StrengthLevel;
  /** Pivot levels (null if disabled) */
  pivotLevels: PivotLevels | null;
  /** Background zone levels for chart shading */
  bgZones: { entry: number; sl: number; tp1: number; tp2: number; direction: "long" | "short" } | null;
}

export const trendMonsterIndicator: IndicatorDefinition = {
  id: "trend_monster",
  name: "Trend Monster",
  shortName: "TMN",
  category: "strategies",
  pane: "overlay",
  params: [
    { key: "alertEnabled", label: "Alert Notification", type: "boolean", default: true },
    { key: "preset", label: "Strategy", type: "select", default: "standard", options: [
      { label: "Standard", value: "standard" },
      { label: "Long Trend", value: "long_trend" },
      { label: "NSE Segment", value: "nse" },
    ]},
    { key: "displayLevels", label: "Display Lines", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "showBackground", label: "Candle Color", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "showPivot", label: "Pivot", type: "select", default: "no", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "showStrength", label: "Trend Scanner", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
    { key: "lineWidth", label: "Line Width", type: "number", default: 1, min: 1, max: 4, step: 1 },
    { key: "showTrailingStop", label: "Show Moving SL", type: "select", default: "yes", options: [
      { label: "Yes", value: "yes" }, { label: "No", value: "no" },
    ]},
  ],
  plots: [
    { key: "entry", label: "Entry", color: "#FFFFFF", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "stopLoss", label: "Stop Loss", color: "#FFFF00", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target1", label: "Target 1", color: "#32CD32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
    { key: "target2", label: "Target 2", color: "#32CD32", style: "line", lineWidth: 2, lineStyle: 2, lastValueVisible: false },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const presetKey = params.preset ?? "standard";
    const displayLevels = params.displayLevels ?? "yes";
    const preset = PRESETS[presetKey] || PRESETS.standard;
    const len = candles.length;
    const minBars = Math.max(preset.st1Period, preset.st2Period) + 2;

    const emptyResult: PlotData[] = [
      { key: "entry", data: [] }, { key: "stopLoss", data: [] },
      { key: "target1", data: [] }, { key: "target2", data: [] },
    ];

    if (len < minBars) return emptyResult;

    const tick = inferTickSize(candles);
    const lw = Math.max(1, Math.min(4, Math.round(params.lineWidth ?? 1)));

    // ════════════════════════════════════════════════════════════
    // 1. Dual SuperTrend
    // ════════════════════════════════════════════════════════════
    const st1 = computeSuperTrend(candles, preset.st1Period, preset.st1Mult);
    const st2 = computeSuperTrend(candles, preset.st2Period, preset.st2Mult);

    // Combined direction: signal only when both agree
    const combined: Direction[] = new Array(len).fill(DIR_NONE);
    for (let i = 0; i < len; i++) {
      if (st1.direction[i] === st2.direction[i]) {
        combined[i] = st1.direction[i];
      } else {
        combined[i] = DIR_NONE; // neutral
      }
    }

    // ════════════════════════════════════════════════════════════
    // 2. Signal Generation — direction change detection
    // ════════════════════════════════════════════════════════════
    const markers: IndicatorMarker[] = [];
    const arrowMarkers: { time: number; direction: "long" | "short"; price: number; type: string; position: string }[] = [];
    const SIGNAL: Direction[] = new Array(len).fill(DIR_NONE);
    const ENTRY: number[] = new Array(len).fill(0);
    const SL: number[] = new Array(len).fill(0);
    const TSL: number[] = new Array(len).fill(0);
    const TP1: number[] = new Array(len).fill(0);
    const TP2: number[] = new Array(len).fill(0);
    const candleColorState: ("long" | "short" | "neutral")[] = new Array(len).fill("neutral");

    let currentSignal: Direction = DIR_NONE;
    let lastSignalBar = -1;
    const lastClosedBar = len - 2;

    for (let i = 1; i <= lastClosedBar; i++) {
      const cur = combined[i];
      const prev = combined[i - 1];

      let isNewSignal = false;
      if (cur !== DIR_NONE && cur !== currentSignal) {
        // New signal — direction changed
        isNewSignal = true;
        currentSignal = cur;
        const entryPrice = candles[i].open !== 0 ? candles[i].close : candles[i].close;
        // MT4: Entry = Open[barIndex - 1] which is the NEXT candle's Open after signal candle completes
        // In our forward approach: entry = Close of signal candle (matches live behavior)
        SIGNAL[i] = cur;
        ENTRY[i] = roundToTick(entryPrice, tick);

        if (cur === DIR_LONG) {
          SL[i] = roundToTick(ENTRY[i] * (100.0 - SL_PERCENT) / 100.0, tick);
          TP1[i] = roundToTick(ENTRY[i] * (100.0 + TP1_PERCENT) / 100.0, tick);
          TP2[i] = roundToTick(ENTRY[i] * (100.0 + TP2_PERCENT) / 100.0, tick);
          TSL[i] = 0;

          markers.push({
            time: candles[i].time, position: "belowBar",
            color: "#008000", shape: "arrowUp", text: "", size: 1,
          });
          arrowMarkers.push({ time: candles[i].time, direction: "long", price: candles[i].low, type: "signal", position: "below" });
        } else {
          SL[i] = roundToTick(ENTRY[i] * (100.0 + SL_PERCENT) / 100.0, tick);
          TP1[i] = roundToTick(ENTRY[i] * (100.0 - TP1_PERCENT) / 100.0, tick);
          TP2[i] = roundToTick(ENTRY[i] * (100.0 - TP2_PERCENT) / 100.0, tick);
          TSL[i] = 0;

          markers.push({
            time: candles[i].time, position: "aboveBar",
            color: "#FF0000", shape: "arrowDown", text: "", size: 1,
          });
          arrowMarkers.push({ time: candles[i].time, direction: "short", price: candles[i].high, type: "signal", position: "above" });
        }
        lastSignalBar = i;
      } else if (cur === DIR_NONE && currentSignal !== DIR_NONE) {
        // STs disagree — go neutral, close any position
        currentSignal = DIR_NONE;
        SIGNAL[i] = DIR_NONE;
        ENTRY[i] = 0; SL[i] = 0; TSL[i] = 0; TP1[i] = 0; TP2[i] = 0;
      } else {
        SIGNAL[i] = currentSignal;
        ENTRY[i] = ENTRY[i - 1] || 0;
        SL[i] = SL[i - 1] || 0;
        TP1[i] = TP1[i - 1] || 0;
        TP2[i] = TP2[i - 1] || 0;
      }

      // Trailing stop logic — percentage-based ratchet with activation threshold.
      // Skip on the signal bar itself (TSL is 0/inactive at trade open; using TSL[i-1]
      // here would inherit the previous trade's TSL, which has the wrong direction).
      if (!isNewSignal && lastSignalBar >= 0 && ENTRY[i] > 0) {
        const sigDir = SIGNAL[lastSignalBar];
        const eV = ENTRY[lastSignalBar];
        if (sigDir === DIR_LONG) {
          // Activate TSL once price moves TSL_ACTIVATE_PERCENT above entry
          const activationPrice = roundToTick(eV * (100.0 + TSL_ACTIVATE_PERCENT) / 100.0, tick);
          const candidate = roundToTick(candles[i].close * (100.0 - TSL_PERCENT) / 100.0, tick);
          if (candles[i].high >= activationPrice) {
            if (TSL[i - 1] === 0 || TSL[i - 1] === undefined) {
              TSL[i] = candidate;
            } else {
              TSL[i] = candidate >= TSL[i - 1] ? candidate : TSL[i - 1];
            }
          } else {
            TSL[i] = TSL[i - 1] || 0;
          }
        } else if (sigDir === DIR_SHORT) {
          const activationPrice = roundToTick(eV * (100.0 - TSL_ACTIVATE_PERCENT) / 100.0, tick);
          const candidate = roundToTick(candles[i].close * (100.0 + TSL_PERCENT) / 100.0, tick);
          if (candles[i].low <= activationPrice) {
            if (TSL[i - 1] === 0 || TSL[i - 1] === undefined) {
              TSL[i] = candidate;
            } else {
              TSL[i] = candidate <= TSL[i - 1] ? candidate : TSL[i - 1];
            }
          } else {
            TSL[i] = TSL[i - 1] || 0;
          }
        }
      } else if (!isNewSignal) {
        TSL[i] = 0;
      }

      // Candle color state
      if (currentSignal === DIR_LONG) candleColorState[i] = "long";
      else if (currentSignal === DIR_SHORT) candleColorState[i] = "short";
      else candleColorState[i] = "neutral";
    }

    // Forward-fill to forming bar
    if (lastClosedBar >= 0 && lastClosedBar < len - 1) {
      for (let i = lastClosedBar + 1; i < len; i++) {
        SIGNAL[i] = SIGNAL[lastClosedBar]; ENTRY[i] = ENTRY[lastClosedBar];
        SL[i] = SL[lastClosedBar]; TSL[i] = TSL[lastClosedBar];
        TP1[i] = TP1[lastClosedBar]; TP2[i] = TP2[lastClosedBar];
        candleColorState[i] = candleColorState[lastClosedBar];
      }
    }

    // ════════════════════════════════════════════════════════════
    // 3. Target / SL hit detection — bar-by-bar sequential
    // ════════════════════════════════════════════════════════════
    let status: TrendMonsterDashboard["status"] = "Active";
    let tp1Hit = false, tp2Hit = false, slHit = false, tslHit = false;
    let bookedPL = 0;
    let tp1HitBar = -1, tp2HitBar = -1, slHitBar = -1, tslHitBar = -1;

    if (lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const eV = ENTRY[lastSignalBar], slV = SL[lastSignalBar];
      const tp1V = TP1[lastSignalBar], tp2V = TP2[lastSignalBar];
      const sigDir = SIGNAL[lastSignalBar];

      // Include live bar (len-1) so status updates in real-time on price touch
      for (let i = lastSignalBar + 1; i < len; i++) {
        if (sigDir === DIR_LONG) {
          // Check SL first (worst case)
          if (!slHit && !tslHit && candles[i].low <= slV) {
            slHit = true; slHitBar = i; status = "Stop Loss Hit"; bookedPL = slV - eV;
            break; // position closed
          }
          // Check TSL hit
          if (!slHit && !tslHit && TSL[i] > 0 && candles[i].low <= TSL[i]) {
            tslHit = true; tslHitBar = i; status = "Trailing SL Hit"; bookedPL = TSL[i] - eV;
            break; // position closed
          }
          // Check TP1
          if (!tp1Hit && candles[i].high >= tp1V) {
            tp1Hit = true; tp1HitBar = i; status = "Target1 Hit"; bookedPL = tp1V - eV;
          }
          // Check TP2
          if (!tp2Hit && candles[i].high >= tp2V) {
            tp2Hit = true; tp2HitBar = i; status = "Target2 Hit"; bookedPL = tp2V - eV;
            break; // full target achieved
          }
        } else if (sigDir === DIR_SHORT) {
          if (!slHit && !tslHit && candles[i].high >= slV) {
            slHit = true; slHitBar = i; status = "Stop Loss Hit"; bookedPL = eV - slV;
            break;
          }
          if (!slHit && !tslHit && TSL[i] > 0 && candles[i].high >= TSL[i]) {
            tslHit = true; tslHitBar = i; status = "Trailing SL Hit"; bookedPL = eV - TSL[i];
            break;
          }
          if (!tp1Hit && candles[i].low <= tp1V) {
            tp1Hit = true; tp1HitBar = i; status = "Target1 Hit"; bookedPL = eV - tp1V;
          }
          if (!tp2Hit && candles[i].low <= tp2V) {
            tp2Hit = true; tp2HitBar = i; status = "Target2 Hit"; bookedPL = eV - tp2V;
            break;
          }
        }
      }

      // After loop: if a target was achieved, bookedPL should reflect the
      // highest target hit — not an SL/TSL exit that happened after.
      if (tp2Hit) {
        bookedPL = sigDir === DIR_LONG ? tp2V - eV : eV - tp2V;
      } else if (tp1Hit) {
        bookedPL = sigDir === DIR_LONG ? tp1V - eV : eV - tp1V;
      }
    }

    // ════════════════════════════════════════════════════════════
    // 4. Dashboard metadata + Strength Meter + Pivots + Bg Zones
    // ════════════════════════════════════════════════════════════
    const showBackground = params.showBackground ?? "yes";
    const showPivot = params.showPivot ?? "no";
    const showStrength = params.showStrength ?? "yes";
    const showTrailingStop = params.showTrailingStop ?? "yes";

    const uiBar = Math.max(0, len - 2);
    const currentPrice = candles[len - 1]?.close ?? 0;
    const currSignal = SIGNAL[uiBar];
    const currEntry = ENTRY[uiBar];
    const currSL = SL[uiBar];
    const currTSL = TSL[uiBar];
    const currTP1 = TP1[uiBar];
    const currTP2 = TP2[uiBar];

    let dashSignal: TrendMonsterDashboard["signal"] = null;
    let dashTrend: TrendMonsterDashboard["trend"] = "NEUTRAL";
    let livePL = 0;

    if (currSignal !== DIR_NONE && currEntry > 0) {
      if (currSignal === DIR_LONG) {
        dashSignal = "BUY"; dashTrend = "LONG"; livePL = currentPrice - currEntry;
      } else {
        dashSignal = "SELL"; dashTrend = "SHORT"; livePL = currEntry - currentPrice;
      }
    } else if (lastSignalBar >= 0 && SIGNAL[lastSignalBar] !== DIR_NONE) {
      // Neutral phase after trade closed — persist last signal direction so dashboard
      // still shows BUY/SELL badge and card color instead of going blank.
      dashSignal = SIGNAL[lastSignalBar] === DIR_LONG ? "BUY" : "SELL";
      dashTrend  = SIGNAL[lastSignalBar] === DIR_LONG ? "LONG" : "SHORT";
      livePL = 0; // trade is closed
    }

    // ── Strength Meter (5-factor score) ──
    let strengthScore = -1; // -1 = hidden (showStrength=no)
    if (showStrength === "yes" && len > 200) {
      strengthScore = 0;
      const rsi14 = computeRSI(candles, 14);
      const ema20 = computeEMA(candles, 20);
      const ema50 = computeEMA(candles, 50);
      const ema200 = computeEMA(candles, 200);
      const rsiVal = rsi14[uiBar];
      const c = candles[uiBar].close;

      // Factor 1: ST1 direction aligned with current trend
      if ((dashTrend === "LONG" && st1.direction[uiBar] === DIR_LONG) ||
          (dashTrend === "SHORT" && st1.direction[uiBar] === DIR_SHORT)) strengthScore++;
      // Factor 2: ST2 direction aligned with current trend
      if ((dashTrend === "LONG" && st2.direction[uiBar] === DIR_LONG) ||
          (dashTrend === "SHORT" && st2.direction[uiBar] === DIR_SHORT)) strengthScore++;
      // Factor 3: RSI bullish (>50) or bearish (<50)
      if ((dashTrend === "LONG" && rsiVal > 50) || (dashTrend === "SHORT" && rsiVal < 50)) strengthScore++;
      // Factor 4: Price above/below EMA(20) + EMA(50) alignment
      if ((dashTrend === "LONG" && c > ema20[uiBar] && ema20[uiBar] > ema50[uiBar]) ||
          (dashTrend === "SHORT" && c < ema20[uiBar] && ema20[uiBar] < ema50[uiBar])) strengthScore++;
      // Factor 5: EMA(50) above/below EMA(200) — macro trend
      if ((dashTrend === "LONG" && ema50[uiBar] > ema200[uiBar]) ||
          (dashTrend === "SHORT" && ema50[uiBar] < ema200[uiBar])) strengthScore++;
    }

    // ── Pivot Levels (Standard Pivot Points from previous day's OHLC) ──
    let pivotLevels: PivotLevels | null = null;
    if (showPivot === "yes" && len > 10) {
      // Find previous day's OHLC using IST date boundary
      let prevDayH = 0, prevDayL = Infinity, prevDayC = 0;
      let currentDay = getISTDate(candles[len - 1].time);
      let foundPrevDay = false;
      for (let i = len - 1; i >= 0; i--) {
        const d = getISTDate(candles[i].time);
        if (d < currentDay) {
          if (!foundPrevDay) {
            foundPrevDay = true;
            prevDayH = candles[i].high;
            prevDayL = candles[i].low;
            prevDayC = candles[i].close;
          } else if (d === getISTDate(candles[i].time)) {
            prevDayH = Math.max(prevDayH, candles[i].high);
            prevDayL = Math.min(prevDayL, candles[i].low);
          } else {
            break; // went past previous day
          }
        }
      }
      if (foundPrevDay && prevDayH > 0) {
        pivotLevels = computePivotLevels(prevDayH, prevDayL, prevDayC);
      }
    }

    // ── Background Zones (Entry/SL/TP areas) ──
    let bgZones: TrendMonsterDashboard["bgZones"] = null;
    if (showBackground === "yes" && currEntry > 0 && currSL > 0) {
      bgZones = {
        entry: currEntry,
        sl: currSL,
        tp1: currTP1,
        tp2: currTP2,
        direction: currSignal === DIR_LONG ? "long" : "short",
      };
    }

    const dashboard: TrendMonsterDashboard = {
      signal: dashSignal, trend: dashTrend,
      // When neutral (STs disagree / trade closed), fall back to lastSignalBar values so
      // the dashboard still shows the last trade's levels for reference.
      entry:     currEntry > 0 ? currEntry     : (lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0 ? ENTRY[lastSignalBar] : null),
      stopLoss:  currSL    > 0 ? currSL        : (lastSignalBar >= 0 && SL[lastSignalBar]    > 0 ? SL[lastSignalBar]    : null),
      // TSL: when active use live value; when hit-and-closed fall back to value at hit bar.
      trailingSL: currTSL > 0 ? currTSL : (tslHit && tslHitBar >= 0 ? TSL[tslHitBar] : null),
      target1:   currTP1   > 0 ? currTP1       : (lastSignalBar >= 0 && TP1[lastSignalBar]   > 0 ? TP1[lastSignalBar]   : null),
      target2:   currTP2   > 0 ? currTP2       : (lastSignalBar >= 0 && TP2[lastSignalBar]   > 0 ? TP2[lastSignalBar]   : null),
      currentPrice, livePL, bookedPL, status,
      tp1Hit, tp2Hit, slHit, tslHit,
      preset: presetKey,
      candleState: candleColorState[uiBar] || "neutral",
      strengthScore,
      strengthLabel: computeStrengthLabel(strengthScore),
      pivotLevels,
      bgZones,
    };

    // ════════════════════════════════════════════════════════════
    // 5. Build plot data + candle colors + hit markers
    // ════════════════════════════════════════════════════════════
    const entryData: { time: number; value: number }[] = [];
    const slData: { time: number; value: number }[] = [];
    const t1Data: { time: number; value: number }[] = [];
    const t2Data: { time: number; value: number }[] = [];
    const tslData: { time: number; value: number }[] = [];

    // Plot series intentionally left empty — price lines (section 7 below) handle all
    // level drawing via RangePriceLinePrimitive (entry candle → right edge) + axis badges.

    // MT4 candle colors: Green for long, Red for short, White for neutral (gated on showBackground)
    const candleColors: { time: number; color: string }[] = [];
    if (showBackground === "yes") for (let i = 0; i < len; i++) {
      const state = candleColorState[i];
      if (state === "long") candleColors.push({ time: candles[i].time, color: "#008000" });
      else if (state === "short") candleColors.push({ time: candles[i].time, color: "#FF0000" });
      else candleColors.push({ time: candles[i].time, color: "#808080" });
    }

    // Hit markers with arrow badges
    if (lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const sigDir = SIGNAL[lastSignalBar];
      if (tp1HitBar >= 0) {
        const pos = sigDir === DIR_LONG ? "aboveBar" : "belowBar";
        const aPos = sigDir === DIR_LONG ? "above" : "below";
        markers.push({ time: candles[tp1HitBar].time, position: pos as any, shape: "circle", color: "#32CD32", text: "✓ T1", size: 1 });
        arrowMarkers.push({ time: candles[tp1HitBar].time, direction: sigDir === DIR_LONG ? "long" : "short", price: sigDir === DIR_LONG ? candles[tp1HitBar].high : candles[tp1HitBar].low, type: "tp1", position: aPos });
      }
      if (tp2HitBar >= 0) {
        const pos = sigDir === DIR_LONG ? "aboveBar" : "belowBar";
        const aPos = sigDir === DIR_LONG ? "above" : "below";
        markers.push({ time: candles[tp2HitBar].time, position: pos as any, shape: "circle", color: "#32CD32", text: "✓ T2", size: 1 });
        arrowMarkers.push({ time: candles[tp2HitBar].time, direction: sigDir === DIR_LONG ? "long" : "short", price: sigDir === DIR_LONG ? candles[tp2HitBar].high : candles[tp2HitBar].low, type: "tp2", position: aPos });
      }
      if (slHitBar >= 0) {
        const pos = sigDir === DIR_LONG ? "belowBar" : "aboveBar";
        const aPos = sigDir === DIR_LONG ? "below" : "above";
        markers.push({ time: candles[slHitBar].time, position: pos as any, shape: "circle", color: "#FF0000", text: "✗ SL", size: 1 });
        arrowMarkers.push({ time: candles[slHitBar].time, direction: sigDir === DIR_LONG ? "long" : "short", price: sigDir === DIR_LONG ? candles[slHitBar].low : candles[slHitBar].high, type: "sl", position: aPos });
      }
      if (tslHitBar >= 0) {
        const pos = sigDir === DIR_LONG ? "belowBar" : "aboveBar";
        const aPos = sigDir === DIR_LONG ? "below" : "above";
        markers.push({ time: candles[tslHitBar].time, position: pos as any, shape: "circle", color: "#FFA500", text: "✗ TSL", size: 1 });
        arrowMarkers.push({ time: candles[tslHitBar].time, direction: sigDir === DIR_LONG ? "long" : "short", price: sigDir === DIR_LONG ? candles[tslHitBar].low : candles[tslHitBar].high, type: "tsl", position: aPos });
      }
    }

    // ════════════════════════════════════════════════════════════
    // 7. Price lines — axis badges only from entry candle onward
    // ════════════════════════════════════════════════════════════
    const priceLines: IndicatorPriceLine[] = [];
    if (displayLevels !== "no" && lastSignalBar >= 0 && ENTRY[lastSignalBar] > 0) {
      const fromTime = candles[lastSignalBar].time;
      const entryPL = ENTRY[lastSignalBar];
      const slPL = SL[lastSignalBar];
      const tp1PL = TP1[lastSignalBar];
      const tp2PL = TP2[lastSignalBar];
      priceLines.push(
        { id: "tmn-entry", price: entryPL, color: "#FFFFFF", title: "Entry", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "tmn-sl",    price: slPL,    color: "#FFFF00", title: "SL",    lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "tmn-t1",    price: tp1PL,   color: "#32CD32", title: "TGT-1", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
        { id: "tmn-t2",    price: tp2PL,   color: "#32CD32", title: "TGT-2", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime },
      );
      if (showTrailingStop !== "no" && currTSL > 0) {
        priceLines.push({ id: "tmn-tsl", price: currTSL, color: "#E65100", title: "Moving SL", lineVisible: true, lineWidth: lw, lineStyle: 2, axisLabelVisible: true, fromTime });
      }
    }
    if (showPivot === "yes" && pivotLevels) {
      // Find today's first candle (IST) to anchor pivot lines from start of day
      const todayDate = getISTDate(candles[len - 1].time);
      let pivotFromTime = candles[len - 1].time;
      for (let i = len - 1; i >= 0; i--) {
        if (getISTDate(candles[i].time) === todayDate) {
          pivotFromTime = candles[i].time;
        } else {
          break;
        }
      }
      priceLines.push(
        { id: "tmn-pp", price: pivotLevels.pp, color: "#FFFFFF", title: "Pivot",       lineVisible: true, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, fromTime: pivotFromTime },
        { id: "tmn-r1", price: pivotLevels.r1, color: "#32CD32", title: "Resistance 1", lineVisible: true, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, fromTime: pivotFromTime },
        { id: "tmn-r2", price: pivotLevels.r2, color: "#32CD32", title: "Resistance 2", lineVisible: true, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, fromTime: pivotFromTime },
        { id: "tmn-s1", price: pivotLevels.s1, color: "#FF4444", title: "Support 1",    lineVisible: true, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, fromTime: pivotFromTime },
        { id: "tmn-s2", price: pivotLevels.s2, color: "#FF4444", title: "Support 2",    lineVisible: true, lineWidth: 1, lineStyle: 1, axisLabelVisible: true, fromTime: pivotFromTime },
      );
    }

    return [
      { key: "entry", data: entryData, markers, priceLines, metadata: { dashboard, candleColors, arrowMarkers } },
      { key: "stopLoss", data: slData },
      { key: "target1", data: t1Data },
      { key: "target2", data: t2Data },
    ];
  },
};
